/**
 * Drop-in replacement for react-admin's FilterButton on index pages with a
 * long filter list. The stock button opens a plain menu, which gets hard to
 * scan past a dozen entries; this one opens a type-to-search combo box over
 * the filter labels. Picking a filter toggles it like the stock menu: a hidden
 * filter is shown (and its input focused), a displayed one is removed.
 *
 * Reads the same List `filters` context as react-admin's FilterButton, so it
 * goes in the List's `actions` toolbar — most pages want the ready-made
 * `<SearchableFilterActions />`. Saved queries aren't supported.
 */
import { isValidElement, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import {
  Button,
  TopToolbar,
  useFilterContext,
  useListContext,
  useResourceContext,
  useTranslateLabel,
} from 'react-admin';
import {
  Autocomplete,
  Box,
  Checkbox,
  Divider,
  InputAdornment,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  TextField,
} from '@mui/material';
import type { PopperProps } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';

export interface SearchableFilterButtonProps {
  /**
   * Filters to offer when rendered outside a List. Ignored inside one, where
   * the List's `filters` context takes precedence — same as react-admin's
   * FilterButton.
   */
  filters?: ReactNode[];
  label?: string;
  placeholder?: string;
  /** Popover width, in px. */
  width?: number;
}

interface FilterOption {
  source: string;
  label: string;
  defaultValue: unknown;
  disabled: boolean;
}

interface FilterInputProps {
  source: string;
  label?: ReactNode;
  defaultValue?: unknown;
  alwaysOn?: boolean;
  disabled?: boolean;
}

// Render the option list inline inside the popover rather than in a second
// floating popper, so the search box and its results read as one panel.
const InlinePopper = ({ children }: PopperProps) => (
  <Box>{typeof children === 'function' ? null : children}</Box>
);

// Like react-admin's FilterButton, focus the newly shown input imperatively (it
// has no way of knowing it was just added). A SelectInput's named input is MUI
// Select's hidden native input, so focus its visible combobox instead.
const focusFilterInput = (source: string) => {
  const input = document.querySelector<HTMLInputElement>(`input[name='${source}']`);
  const select = input?.closest('.MuiInputBase-root')?.querySelector<HTMLElement>('[role="combobox"]');
  (select ?? input)?.focus();
};

export const SearchableFilterButton = ({
  filters: filtersProp,
  label = 'Add filter',
  placeholder = 'Search filters',
  width = 300,
}: SearchableFilterButtonProps) => {
  const filters = useFilterContext() ?? filtersProp;
  const resource = useResourceContext();
  const translateLabel = useTranslateLabel();
  const { displayedFilters = {}, filterValues, showFilter, hideFilter, setFilters } = useListContext();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  // Source of a just-shown filter whose input should take focus once the
  // popover has closed (and restored focus to the button).
  const pendingFocusRef = useRef<string | null>(null);
  const [open, setOpen] = useState(false);

  // Labels resolve like the stock menu's: an explicit label (translated), else
  // the resource field's translation, else the humanized source.
  const toOption = (filter: ReactElement<FilterInputProps>): FilterOption => {
    const { source, label: filterLabel, defaultValue, disabled } = filter.props;
    const resolved = translateLabel({ label: filterLabel, source, resource });
    return {
      source,
      label: typeof resolved === 'string' && resolved ? resolved : source,
      defaultValue,
      disabled: !!disabled,
    };
  };

  const options = (filters ?? [])
    .filter((filter): filter is ReactElement<FilterInputProps> =>
      isValidElement<FilterInputProps>(filter) &&
      typeof filter.props.source === 'string' &&
      !filter.props.alwaysOn)
    .map(toOption);
  const hasFilterValues = Object.keys(filterValues ?? {}).length > 0;

  if (!options.length && !hasFilterValues) return null;

  const toggle = (option: FilterOption) => {
    // MUI only blocks clicks on disabled options via CSS pointer-events.
    if (option.disabled) return;
    setOpen(false);
    if (displayedFilters[option.source]) {
      hideFilter(option.source);
      return;
    }
    showFilter(option.source, option.defaultValue === '' ? undefined : option.defaultValue);
    pendingFocusRef.current = option.source;
  };

  // Focus the new filter only after the popover has finished closing, or its
  // focus restore sends focus back to the button.
  const focusPendingFilter = () => {
    const source = pendingFocusRef.current;
    pendingFocusRef.current = null;
    if (source) focusFilterInput(source);
  };

  return (
    <>
      <Button
        ref={anchorRef}
        label={label}
        aria-haspopup="listbox"
        onClick={() => setOpen(true)}
      >
        <FilterListIcon />
      </Button>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width } } }}
        // Focus the search box ourselves: the focus trap would otherwise focus
        // the paper, and the input's autoFocus alone doesn't survive a real
        // mouse click (focus stays on the button). onEntering runs as soon as
        // the popover mounts, so typing straight after the click lands in the
        // search box; onEntered retries once the open transition ends.
        disableAutoFocus
        TransitionProps={{
          onEntering: () => searchRef.current?.focus(),
          onEntered: () => searchRef.current?.focus(),
          onExited: focusPendingFilter,
        }}
      >
        <Autocomplete<FilterOption>
          open
          // Enter picks the top match straight after typing.
          autoHighlight
          options={options}
          value={null}
          onChange={(_event, option) => option && toggle(option)}
          onClose={(_event, reason) => reason === 'escape' && setOpen(false)}
          getOptionLabel={option => option.label}
          getOptionDisabled={option => option.disabled}
          isOptionEqualToValue={(option, value) => option.source === value.source}
          noOptionsText="No matching filters"
          // The list is always open, so the popup chevron does nothing.
          forcePopupIcon={false}
          disablePortal
          PopperComponent={InlinePopper}
          ListboxProps={{ sx: { maxHeight: 360 } }}
          renderOption={({ key, ...props }, option) => (
            <li key={key} {...props}>
              <Checkbox
                size="small"
                edge="start"
                checked={!!displayedFilters[option.source]}
                tabIndex={-1}
                disableRipple
                sx={{ py: 0 }}
              />
              {option.label}
            </li>
          )}
          renderInput={params => (
            <TextField
              {...params}
              // InputBase forks this with Autocomplete's own inputProps.ref.
              inputRef={searchRef}
              autoFocus
              placeholder={placeholder}
              size="small"
              sx={{ p: 1 }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        {hasFilterValues && (
          <>
            <Divider />
            {/* A button, not a MenuItem: outside a MenuList a MenuItem isn't
                tabbable, so keyboard users couldn't reach it. */}
            <ListItemButton
              onClick={() => {
                setFilters({}, {});
                setOpen(false);
              }}
            >
              <ListItemIcon>
                <ClearIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Remove all filters</ListItemText>
            </ListItemButton>
          </>
        )}
      </Popover>
    </>
  );
};

/**
 * The List `actions` toolbar for a page that only needs filtering — what
 * react-admin's default actions give an `exporter={false}` list, with the
 * searchable button. Extra actions (create, export…) go in `children`.
 */
export const SearchableFilterActions = ({
  children,
  ...buttonProps
}: SearchableFilterButtonProps & { children?: ReactNode }) => (
  <TopToolbar>
    <SearchableFilterButton {...buttonProps} />
    {children}
  </TopToolbar>
);
